import React, { useRef, useEffect, useState, useMemo } from 'react';

import { TextField, FormControl, FormHelperText } from '@material-ui/core';
import { Autocomplete as AutocompleteUI } from '@material-ui/lab';
import { useField } from '@unform/core';
import { isEmpty, find, isEqual, isFunction } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Hidden } from '~/components/Form';
import { canURL } from '~/helpers';
import api from '~/services/api';

const sameUniqueValue = (a, b) =>
  isEqual(a, b) || (a != null && b != null && String(a) === String(b));

function Autocomplete({
  name,
  label,
  title,
  getValue,
  serialize,
  url,
  params,
  unique,
  hiddens,
  first,
  disabled,
  excluded,
  variant,
  onSerialize,
  isGrid,
  onLoadingChange,
  onLoadingError,
  forcedValue,
  ...rest
}) {
  const inputRef = useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const [data, setData] = useState([]);
  const [value, setValue] = useState(null);
  const [fieldHidden, setFieldHidden] = useState(hiddens);
  const [loading, setLoading] = useState(false);

  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);

  const renderItemsHidden = useMemo(
    () =>
      fieldHidden.map((field) => (
        <Hidden
          name={field.name}
          key={field.name}
          value={field.value || ''}
        />
      )),
    [fieldHidden]
  );

  function clearAutocompleteValue() {
    const defValue = {
      [label]: null,
      [unique]: null,
    };

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    setValue(null);
    getValue(serialize(defValue));
    setFieldHidden(hiddens);
  }

  function handleChangeValue(newValue) {
    const defValue = {
      [label]: null,
      [unique]: null,
    };

    if (inputRef.current) {
      inputRef.current.value = newValue
        ? serialize(newValue[unique])
        : '';
    }

    setValue(newValue || null);
    getValue(serialize(newValue || defValue));

    if (!isEmpty(fieldHidden) && newValue) {
      const newHiddens = hiddens.map((hidden) => ({
        ...hidden,
        value: newValue[hidden.name],
      }));

      setFieldHidden(newHiddens);
    } else {
      setFieldHidden(hiddens);
    }
  }

  async function handleData() {
    try {
      onLoadingChange(true);
      setLoading(true);

      const response = await api.get(url, {
        params: {
          ...params,
          pagesize: 99999,
          pesquisalista: true,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });

      let modItems = [];

      if (!isEmpty(response.data)) {
        const { items = [] } = response.data;

        if (isFunction(onSerialize)) {
          const serializedItems = onSerialize(response.data) || [];

          modItems = [...first, ...serializedItems].filter(
            (item) =>
              item &&
              !excluded.some((excludedValue) =>
                sameUniqueValue(item[unique], excludedValue)
              )
          );
        } else {
          modItems = [...first, ...items].filter(
            (item) =>
              item &&
              !excluded.some((excludedValue) =>
                sameUniqueValue(item[unique], excludedValue)
              )
          );
        }
      }

      setData(modItems);

      if (defaultValue !== null && defaultValue !== undefined) {
        const findItem = find(
          modItems,
          (item) =>
            item != null &&
            item[unique] !== undefined &&
            sameUniqueValue(item[unique], defaultValue)
        );

        if (findItem) {
          if (inputRef.current) {
            inputRef.current.value = serialize(findItem[unique]);
          }

          setValue(findItem);
          getValue(serialize(findItem));
        } else {
          clearAutocompleteValue();
        }
      }
    } catch (err) {
      onLoadingError(err);
      console.log('Autocomplete - handleData - Error:', err);
    } finally {
      setLoading(false);
      onLoadingChange(false);
    }
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    handleData();
  }, [url, paramsKey]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value =
        defaultValue !== null && defaultValue !== undefined
          ? defaultValue
          : '';
    }
  }, [defaultValue]);

  useEffect(() => {
    if (forcedValue === undefined || forcedValue === null) {
      return;
    }

    const forcedItem = find(
      data,
      (item) =>
        item != null &&
        item[unique] !== undefined &&
        sameUniqueValue(item[unique], forcedValue)
    );

    if (!forcedItem) {
      const defValue = {
        [label]: null,
        [unique]: null,
      };

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      setValue(null);
      getValue(serialize(defValue));
      setFieldHidden(hiddens);

      return;
    }

    if (inputRef.current) {
      inputRef.current.value = serialize(forcedItem[unique]);
    }

    setValue(forcedItem);
    getValue(serialize(forcedItem));

    if (!isEmpty(hiddens)) {
      const newHiddens = hiddens.map((hidden) => ({
        ...hidden,
        value: forcedItem[hidden.name],
      }));

      setFieldHidden(newHiddens);
    }
  }, [
    data,
    forcedValue,
    unique,
    serialize,
    label,
    getValue,
    hiddens,
  ]);

  useEffect(() => {
    if (disabled) {
      clearError();
    }
  }, [disabled, clearError]);

  return (
    <FormControl style={{ width: '100%' }}>
      <AutocompleteUI
        {...rest}
        size="small"
        options={data}
        value={value}
        loading={loading}
        getOptionLabel={(option) => {
          if (
            !option ||
            option[label] === null ||
            option[label] === undefined
          ) {
            return '';
          }

          return String(option[label]);
        }}
        getOptionSelected={(option, selectedValue) =>
          option &&
          selectedValue &&
          sameUniqueValue(option[unique], selectedValue[unique])
        }
        renderTags={() => { }}
        onChange={(event, newValue) => {
          handleChangeValue(newValue);
        }}
        disabled={(permission || disabled) && !isGrid}
        onFocus={clearError}
        renderInput={(inputProps) => (
          <TextField
            {...inputProps}
            ref={inputRef}
            error={!!error}
            label={title}
            variant={variant}
            fullWidth
            disabled={(permission || disabled) && !isGrid}
          />
        )}
      />

      {error && <FormHelperText error>{error}</FormHelperText>}

      {renderItemsHidden}
    </FormControl>
  );
}

Autocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  serialize: PropTypes.func,
  url: PropTypes.string.isRequired,
  params: PropTypes.objectOf(PropTypes.any),
  unique: PropTypes.string.isRequired,
  hiddens: PropTypes.arrayOf(PropTypes.any),
  first: PropTypes.arrayOf(PropTypes.any),
  disabled: PropTypes.bool,
  excluded: PropTypes.arrayOf(PropTypes.any),
  variant: PropTypes.string,
  onSerialize: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  isGrid: PropTypes.bool,
  onLoadingChange: PropTypes.func,
  onLoadingError: PropTypes.func,
  forcedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

Autocomplete.defaultProps = {
  getValue: () => { },
  serialize: (value) => value,
  params: {},
  hiddens: [],
  first: [],
  disabled: false,
  excluded: [],
  variant: 'outlined',
  onSerialize: false,
  isGrid: false,
  onLoadingChange: () => { },
  onLoadingError: () => { },
  forcedValue: undefined,
};

export default Autocomplete;
